import { BACKGAMMON_GAME_ID, CR_GAME_ID } from '@/lib/gameFeatures';
import { BACKGAMMON_PROFILE_TYPE } from '@/lib/qgammon/lobbyTornei';
import { selezionaGioco } from '@/lib/selezionaGioco';
import type { AppDispatch } from '@/store';
import { setHubSection } from '@/store/core.slice';

import type { SezioneDellaCoda } from './azionePersistente';
import type { PresentazioneDiCoda } from './presentazione';

/**
 * «Torna alla coda» / «Torna ai sit&go» (r15): dopo una partita nata da una coda o da un sit&go si torna alla scheda della
 * Hub dove si aspetta, non alla scheda di un torneo. Il posto e' lo stesso a cui porta «Vai» della barra della coda
 * (`BarraCoda`): il gioco e la scheda della Hub (`'ranked'` | `'sitngo'`).
 */
export interface DoveTornare {
  gioco: 'cr' | 'bg';
  sezione: SezioneDellaCoda;
}

/**
 * Dove si torna. Pura: il catalogo e le serrature le passa chi chiama.
 *  · backgammon (solo col suo pannello acceso, `conScheda`): il suo «Sit&Go». Il backgammon non ha la coda 1v1 di Limulo
 *    (Luca, I-340): la sua scheda `'ranked'` e' la «Gioca» nativa di QGammon, che di questa partita non dice niente;
 *  · Clash Royale: la «Ranked» per una coda; il «Sit&Go» per un sit&go, ma solo se il suo pannello e' acceso in questa build
 *    (`sitngoCrAcceso`), altrimenti la «Ranked», che li racconta tutti per nome;
 *  · catalogo non letto o gioco che il catalogo non nomina ⇒ Clash Royale, com'e' sempre stato.
 */
export function doveTornareOf(input: {
  presentazione: PresentazioneDiCoda;
  /** Il `profileType` del gioco del torneo (dal catalogo), o `null` se non si sa. */
  profileType: string | null;
  sitngoCrAcceso: boolean;
  /** `giochiConCodeDaRiprendere()`: i giochi che hanno in questa build un pannello delle code. */
  conScheda: readonly string[];
}): DoveTornare {
  if (input.profileType === BACKGAMMON_PROFILE_TYPE && input.conScheda.includes(BACKGAMMON_PROFILE_TYPE)) {
    return { gioco: 'bg', sezione: 'sitngo' };
  }
  return {
    gioco: 'cr',
    sezione: input.presentazione === 'sit-and-go' && input.sitngoCrAcceso ? 'sitngo' : 'ranked',
  };
}

/**
 * Apre la scheda della Hub di `dove`. Una definizione sola, per «Vai» della barra della coda e per «Torna alla coda» della
 * modale: le schede della Hub sono quelle del gioco ATTIVO, e `'ranked'` col backgammon attivo e' la sua «Gioca».
 * Chi chiama naviga poi alla Hub (`/`).
 */
export function apriSchedaDellaCoda(
  dispatch: AppDispatch,
  dove: DoveTornare,
  giocoAttivoId: number | null,
): void {
  if (dove.gioco === 'bg') {
    selezionaGioco(dispatch, BACKGAMMON_GAME_ID);
    dispatch(setHubSection('sitngo'));
    return;
  }
  // Con un altro gioco attivo si porta prima a Clash Royale; con CR gia' attivo (o gioco non noto) non si tocca niente.
  if (giocoAttivoId !== null && giocoAttivoId !== CR_GAME_ID) selezionaGioco(dispatch, CR_GAME_ID);
  dispatch(setHubSection(dove.sezione));
}
